import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { ArrowRight, Calendar } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const topicLabel = (t) => {
  if (t === 'web-dev') return 'Web design';
  if (t === 'app-dev') return 'App development';
  if (t === 'behind-the-scenes') return 'Behind the scenes';
  return 'Insight';
};

const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Blog | TryWeb';
    fetch(`${BACKEND}/api/blog/posts`)
      .then((r) => r.json())
      .then((data) => {
        setPosts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <PageLayout>
      <section className="py-24 hero-glow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] text-primary mb-3">Blog</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              <span className="text-gradient">Things I've learned, shipped or thought about.</span>
            </h1>
            <p className="text-xl text-muted-foreground">
              Notes on web design, app development, and the products we build at TryWeb.
              A new post most days.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
              <p className="text-muted-foreground">
                No posts yet — first one ships soon. Bookmark this page or follow on socials.
              </p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Link
                  to={`/blog/${featured.slug}`}
                  className="group block mb-16"
                  data-testid={`blog-featured-${featured.slug}`}
                >
                  <div className="grid lg:grid-cols-2 gap-8 items-center bg-card/40 border border-white/5 rounded-3xl overflow-hidden card-hover">
                    {featured.cover_image_url && (
                      <div className="aspect-[16/10] overflow-hidden bg-zinc-900/20">
                        <img
                          src={featured.cover_image_url.startsWith('http') ? featured.cover_image_url : `${BACKEND}${featured.cover_image_url}`}
                          alt={featured.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="p-8 lg:p-12">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                        <span className="px-2.5 py-1 rounded-full bg-primary/15 text-primary font-medium">
                          {topicLabel(featured.topic)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(featured.published_at)}
                        </span>
                      </div>
                      <h2 className="text-3xl font-bold mb-4 group-hover:text-primary transition-colors">
                        {featured.title}
                      </h2>
                      <p className="text-muted-foreground mb-6">{featured.excerpt}</p>
                      <span className="inline-flex items-center gap-2 text-primary font-medium">
                        Read post <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              )}

              {/* Rest */}
              {rest.length > 0 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {rest.map((p) => (
                    <Link
                      key={p.id}
                      to={`/blog/${p.slug}`}
                      className="group bg-card/40 border border-white/5 rounded-2xl overflow-hidden card-hover"
                      data-testid={`blog-card-${p.slug}`}
                    >
                      {p.cover_image_url && (
                        <div className="aspect-video overflow-hidden bg-zinc-900/20">
                          <img
                            src={p.cover_image_url.startsWith('http') ? p.cover_image_url : `${BACKEND}${p.cover_image_url}`}
                            alt={p.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                          <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary font-medium">
                            {topicLabel(p.topic)}
                          </span>
                          <span>{formatDate(p.published_at)}</span>
                        </div>
                        <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {p.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </PageLayout>
  );
};

export default BlogListPage;
